import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../api/client';
import { inactividadVencida, marcarActividad, borrarActividad } from '../utils/inactividad';

export const AuthContext = createContext();

// Cerrar el navegador ya no cierra la sesion; si al volver pasaron mas de 2 hs
// sin uso, se descarta la sesion guardada antes del primer render.
function descartarSesionInactiva() {
  if (!inactividadVencida()) return;
  ['token', 'refreshToken', 'usuario'].forEach(k => localStorage.removeItem(k));
  borrarActividad();
}

export const AuthProvider = ({ children }) => {
  // Cargamos inicial desde localStorage (esto es síncrono y suficiente)
  const [token, setToken] = useState(() => {
    descartarSesionInactiva();
    return localStorage.getItem('token');
  });
  const [usuario, setUsuario] = useState(() => {
    const s = localStorage.getItem('usuario');
    return s ? JSON.parse(s) : null;
  });
  const [isAuthReady, setIsAuthReady] = useState(false);

  // Marcamos listo en el primer render (ya tenemos lo de localStorage arriba)
  useEffect(() => {
    setIsAuthReady(true);
  }, []);

  // Con una sesion guardada, pedir la cookie de /api/uploads: el login y el
  // refresh ya la dejan, pero una sesion abierta antes de que existiera (o con
  // la cookie vencida) veria las imagenes rotas hasta el proximo refresh.
  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    api.post('/auth/archivos', null, { withCredentials: true }).catch(() => {});
  }, []);

  // Registrar callbacks para que ambos interceptores (axios + fetch global) actualicen el estado
  useEffect(() => {
    const onRefreshed = (newToken, newUser) => {
      setToken(newToken);
      if (newUser) setUsuario(newUser);
    };
    api._onTokenRefreshed = onRefreshed;
    window.__onTokenRefreshed = onRefreshed;
    return () => {
      api._onTokenRefreshed = null;
      window.__onTokenRefreshed = null;
    };
  }, []);

  // Persistir token
  useEffect(() => {
    if (token) localStorage.setItem('token', token);
    else localStorage.removeItem('token');
  }, [token]);

  // Persistir usuario
  useEffect(() => {
    if (usuario) localStorage.setItem('usuario', JSON.stringify(usuario));
    else localStorage.removeItem('usuario');
  }, [usuario]);

  // Helper para setear ambos tras login (acepta refreshToken opcional)
  const loginSuccess = useCallback((t, u, rt) => {
    setToken(t);
    setUsuario(u);
    if (rt) localStorage.setItem('refreshToken', rt);
    marcarActividad(true);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUsuario(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('usuario');
    borrarActividad();
    // Limpieza extra opcional
    localStorage.removeItem('role');
    localStorage.removeItem('permisos');
    localStorage.removeItem('fullname');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('computer_name');
  }, []);

  // Helper para actualizar campos del usuario
  const updateUsuario = useCallback((updates) => {
    setUsuario(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // ============================================
  // FUNCIONES DE VERIFICACIÓN DE ACCESOS
  // ============================================

  /**
   * Verifica si el usuario tiene un acceso específico.
   * Los scopes shared.* son transversales: cualquier usuario autenticado los tiene.
   * @param {string} codigo - Código del acceso (ej: 'ops.work-orders:read')
   * @returns {boolean}
   */
  const tieneAcceso = useCallback((codigo) => {
    if (usuario?.role === 'admin') return true;
    if (codigo?.startsWith('shared.')) return true;

    const accesos  = usuario?.accesos  || [];
    const roles    = usuario?.roles    || [];
    const permisos = usuario?.permisos || [];
    return accesos.includes(codigo) || roles.includes(codigo) || permisos.includes(codigo);
  }, [usuario]);

  /**
   * Verifica si el usuario tiene TODOS los accesos especificados
   * @param {string[]} codigos - Array de códigos de acceso
   * @returns {boolean}
   */
  const tieneTodosLosAccesos = useCallback((codigos) => {
    // Admin siempre tiene todos los accesos
    if (usuario?.role === 'admin') {
      return true;
    }

    const accesos  = usuario?.accesos  || [];
    const roles    = usuario?.roles    || [];
    const permisos = usuario?.permisos || [];
    return codigos.every(codigo => accesos.includes(codigo) || roles.includes(codigo) || permisos.includes(codigo));
  }, [usuario]);

  /**
   * Verifica si el usuario tiene AL MENOS UNO de los accesos especificados
   * @param {string[]} codigos - Array de códigos de acceso
   * @returns {boolean}
   */
  const tieneAlgunAcceso = useCallback((codigos) => {
    // Admin siempre tiene todos los accesos
    if (usuario?.role === 'admin') {
      return true;
    }

    const accesos = usuario?.accesos || [];
    return codigos.some(codigo => accesos.includes(codigo));
  }, [usuario]);

  /**
   * Verifica si el usuario tiene un rol específico
   * @param {string} codigo - Código del rol (ej: 'ROL-01')
   * @returns {boolean}
   */
  const tieneRol = useCallback((codigo) => {
    const roles = usuario?.roles || [];
    // Soporta tanto array de strings como array de objetos {codigo, nombre}
    return roles.some(r => (typeof r === 'string' ? r : r.codigo) === codigo);
  }, [usuario]);

  /**
   * Verifica si el usuario tiene un role_key específico (OAuth2-RBAC)
   * @param {string} key - role_key (ej: 'org.admin', 'ops.machinist')
   * @returns {boolean}
   */
  const tieneRolKey = useCallback((key) => {
    const roleKeys = usuario?.role_keys || [];
    return roleKeys.includes(key);
  }, [usuario]);

  /**
   * Verifica si el usuario es administrador (role='admin', ROL-01 o org.admin)
   * @returns {boolean}
   */
  const esAdmin = useCallback(() => {
    if (usuario?.role === 'admin') return true;
    if (tieneRol('ROL-01')) return true;
    return tieneRolKey('org.admin');
  }, [usuario, tieneRol, tieneRolKey]);

  /**
   * Obtiene todos los accesos de un módulo específico
   * @param {string} modulo - Nombre del módulo (ej: 'ordenes', 'partes')
   * @returns {string[]} - Array de acciones disponibles (ej: ['ver', 'crear', 'editar'])
   */
  const accesosDelModulo = useCallback((modulo) => {
    // Admin tiene todos los accesos
    if (usuario?.role === 'admin') {
      return ['ver', 'crear', 'editar', 'eliminar', 'cambiar_estado', 'ver_todas', 'editar_todas', 'eliminar_todas', 'cerrar'];
    }

    const accesos = usuario?.accesos || [];
    return accesos
      .filter(a => a.startsWith(`${modulo}:`))
      .map(a => a.split(':')[1]);
  }, [usuario]);

  // Valores expuestos
  const value = {
    // Estado existente
    usuario,
    setUsuario,
    updateUsuario,
    token,
    setToken,
    loginSuccess,
    logout,
    isAuthReady,

    // Funciones de accesos OAuth2-RBAC
    tieneAcceso,
    tieneTodosLosAccesos,
    tieneAlgunAcceso,
    tieneRol,
    tieneRolKey,
    esAdmin,
    accesosDelModulo,

    // Shortcuts útiles
    accesos: usuario?.accesos || [],
    roles: usuario?.roles || [],
    roleKeys: usuario?.role_keys || [],
    siteRestricted: usuario?.site_restricted || false,
    assignedSites: usuario?.assigned_sites || [],
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);